import { defineStore } from 'pinia'
import { ref, Ref } from 'vue'
import {
  postPasswordResetRequest as postPasswordResetRequestMutation,
  postValidatePasswordResetCode as postValidatePasswordResetCodeMutation,
  postPasswordReset as postPasswordResetMutation
} from '@/queries'
import { useMutation, provideApolloClient } from '@vue/apollo-composable'
import { useErrorsStore } from '@/store/ErrorsStore'
import { sentryCapture } from '@/utils/sentry'
import { apolloClient } from '@/utils/apolloClient'

export const usePasswordResetStore = defineStore('PasswordResetStore', () => {
  const resetCode: Ref<string | null> = ref(null)

  const {
    mutate: postPasswordResetRequest,
    onDone: onPostPasswordResetRequestDone,
    onError: onPostPasswordResetRequestError
  } = provideApolloClient(apolloClient)(() => useMutation(postPasswordResetRequestMutation))
  const {
    mutate: postValidatePasswordResetCode,
    onDone: onPostValidatePasswordResetCodeDone,
    onError: onPostValidatePasswordResetCodeError
  } = provideApolloClient(apolloClient)(() => useMutation(postValidatePasswordResetCodeMutation))
  const {
    mutate: postPasswordReset,
    onDone: onPostPasswordResetDone,
    onError: onPostPasswordResetError
  } = provideApolloClient(apolloClient)(() => useMutation(postPasswordResetMutation))

  const { setAppError } = useErrorsStore()

  onPostValidatePasswordResetCodeDone(result => {
    if (!result.data) {
      return
    }

    const { postValidatePasswordResetCode } = result.data

    if (!postValidatePasswordResetCode.valid) {
      const title = postValidatePasswordResetCode.reason === 'expired' ? 'Your password reset link has expired' : 'Your password reset link is invalid'

      setAppError({
        title,
        message: 'Click <a href="/forgot-password">here</a> to request a new one.'
      })

      resetCode.value = null
    }
  })

  const handleResetCode = async (code: string) => {
    if (!code) {
      return
    }

    resetCode.value = code

    return await postValidatePasswordResetCode({
      code
    })
  }

  const handleError = (error: any) => sentryCapture(error)

  onPostPasswordResetRequestError(error => handleError(error))
  onPostValidatePasswordResetCodeError(error => handleError(error))
  onPostPasswordResetError(error => handleError(error))

  return {
    handleResetCode,
    onPostPasswordResetDone,
    onPostPasswordResetError,
    onPostPasswordResetRequestDone,
    postPasswordResetRequest,
    postValidatePasswordResetCode,
    postPasswordReset,
    resetCode
  }
})
